import { useState, useEffect } from 'react';
import dayjs from 'dayjs';

const useTradeFilters = (trades = []) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredTrades, setFilteredTrades] = useState([]);

  // Extract unique symbols, categories, statuses, and tags, sorted
  const symbols = [...new Set(trades.map(t => t.symbol))].filter(Boolean).sort();
  const categories = [...new Set(trades.map(t => t.category))].filter(Boolean).sort();
  const statuses = [...new Set(trades.map(t => t.status))].filter(Boolean).sort();
  const allTags = [...new Set(trades.flatMap(t => Array.isArray(t.tags) ? t.tags : typeof t.tags === 'string' ? t.tags.split(',').map(tag => tag.trim()) : []))].filter(Boolean).sort();

  const applyFilters = () => {
    let result = [...trades];
    
    if (selectedCategory) {
      result = result.filter(t => t.category === selectedCategory);
    }
    if (selectedSymbol) {
      result = result.filter(t => t.symbol === selectedSymbol);
    }
    if (selectedStatus) {
      result = result.filter(t => t.status === selectedStatus);
    }
    if (selectedStrategy) {
      result = result.filter(t => t.strategyIds?.includes(selectedStrategy));
    }
    if (selectedTags.length > 0) {
      result = result.filter(t => {
        const tradeTags = Array.isArray(t.tags) ? t.tags : typeof t.tags === 'string' ? t.tags.split(',').map(tag => tag.trim()) : [];
        return selectedTags.some(tag => tradeTags.includes(tag));
      });
    }
    if (startDate) {
      result = result.filter(t => dayjs(t.tradeDate).isAfter(startDate.subtract(1, 'day')));
    }
    if (endDate) {
      result = result.filter(t => dayjs(t.tradeDate).isBefore(endDate.add(1, 'day')));
    }
    
    setFilteredTrades(result);
  };

  const handleTimeFilter = (type) => {
    const today = dayjs();
    switch (type) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'yesterday':
        setStartDate(today.subtract(1, 'day'));
        setEndDate(today.subtract(1, 'day'));
        break;
      case 'thisWeek':
        setStartDate(today.startOf('week'));
        setEndDate(today.endOf('week'));
        break;
      case 'lastWeek':
        setStartDate(today.subtract(1, 'week').startOf('week'));
        setEndDate(today.subtract(1, 'week').endOf('week'));
        break;
      case 'thisMonth':
        setStartDate(today.startOf('month'));
        setEndDate(today.endOf('month'));
        break;
      case 'lastMonth':
        setStartDate(today.subtract(1, 'month').startOf('month'));
        setEndDate(today.subtract(1, 'month').endOf('month'));
        break;
      case 'last3Months':
        setStartDate(today.subtract(3, 'month').startOf('month'));
        setEndDate(today.endOf('month'));
        break;
      case 'thisYear':
        setStartDate(today.startOf('year'));
        setEndDate(today.endOf('year'));
        break;
      case 'lastYear':
        setStartDate(today.subtract(1, 'year').startOf('year'));
        setEndDate(today.subtract(1, 'year').endOf('year'));
        break;
      case 'reset':
        setStartDate(null);
        setEndDate(null);
        setSelectedCategory('');
        setSelectedSymbol('');
        setSelectedStrategy('');
        setSelectedStatus('');
        setSelectedTags([]);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    applyFilters();
  }, [trades, selectedCategory, selectedSymbol, selectedStrategy, selectedStatus, selectedTags, startDate, endDate]);

  return {
    // Filter states
    selectedCategory,
    selectedSymbol,
    selectedStrategy,
    selectedStatus,
    selectedTags,
    startDate,
    endDate,
    
    // Filter setters
    setSelectedCategory,
    setSelectedSymbol,
    setSelectedStrategy,
    setSelectedStatus,
    setSelectedTags,
    setStartDate,
    setEndDate,
    
    // Derived data
    filteredTrades,
    symbols,
    categories,
    statuses,
    allTags,
    
    // Handlers
    handleTimeFilter
  };
};

export default useTradeFilters;